import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../contexts/NotificationContext';
import { listAnnouncements, markAnnouncementRead, listNotifications, markNotificationRead, markAllNotificationsRead, markAllAnnouncementsRead } from '../api';
import { Announcement, Notification } from '../types';
import { ANNOUNCEMENT_TYPE_COLORS, NOTIFICATION_TYPE_COLORS, getAnnouncementTypeLabel, getNotificationTypeLabel } from '../constants/notificationLabels';
import { formatTime } from '../utils/formatTime';

const MAX_BADGE_COUNT = 99;

type TabType = 'announcements' | 'notifications';

type PopupItem = {
  id: string;
  kind: 'announcement' | 'notification';
  type: string;
  title: string;
  content?: string;
  priority?: string;
  createdAt?: string | null;
  relatedType?: string;
  relatedId?: string;
};

export default function NotificationBell() {
  const { t } = useTranslation('notifications');
  const navigate = useNavigate();
  const { unread, refreshUnread, latestEvent, clearLatestEvent, latestReadReceipt } = useNotifications();
  const [open, setOpen] = useState(false);
  const [popup, setPopup] = useState<PopupItem | null>(null);
  const [tab, setTab] = useState<TabType>('notifications');
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!latestEvent || (latestEvent.type !== 'announcement' && latestEvent.type !== 'notification')) return;
    const data = latestEvent.data as Partial<Announcement & Notification>;
    if (!data.id || !data.title) return;
    setPopup({
      id: data.id,
      kind: latestEvent.type,
      type: data.type || '',
      title: data.title,
      content: data.content,
      priority: data.priority,
      createdAt: latestEvent.type === 'announcement' ? data.publishedAt : data.createdAt,
      relatedType: data.relatedType,
      relatedId: data.relatedId,
    });
  }, [latestEvent]);

  // 其他窗口/设备标记已读 → 若回执命中当前气泡那条（同类型且 ID 匹配或同类型全读），
  // 同步关闭气泡，避免用户在另一个窗口处理过的消息还挂在这里。无需再调已读接口。
  useEffect(() => {
    if (!latestReadReceipt || !popup) return;
    const { kind, ids, all } = latestReadReceipt;
    if (kind === popup.kind && (all || (ids ?? []).includes(popup.id))) {
      setPopup(null);
      clearLatestEvent();
    }
  }, [latestReadReceipt, popup, clearLatestEvent]);

  // 每次打开时根据未读数智能选择默认 tab
  useEffect(() => {
    if (!open) return;
    if (unread.notifications > 0) setTab('notifications');
    else if (unread.announcements > 0) setTab('announcements');
    else setTab('notifications');
  }, [open, unread.notifications, unread.announcements]);

  // 点击外部关闭
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // 展开时 / 切换 tab 时加载数据
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setError('');
    if (tab === 'announcements') {
      listAnnouncements(1, 10)
        .then(res => setAnnouncements(res.items || []))
        .catch(() => setError(t('bell.loadFailed')))
        .finally(() => setLoading(false));
    } else {
      listNotifications(1, 10)
        .then(res => setNotifications(res.items || []))
        .catch(() => setError(t('bell.loadFailed')))
        .finally(() => setLoading(false));
    }
  }, [open, tab, t]);

  const handleReadAnnouncement = async (id: string) => {
    await markAnnouncementRead(id).catch(() => {});
    setAnnouncements(prev => prev.map(a => a.id === id ? { ...a, isRead: true } : a));
    refreshUnread();
  };

  const handleReadNotification = async (id: string) => {
    await markNotificationRead(id).catch(() => {});
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, readAt: new Date().toISOString() } : n));
    refreshUnread();
  };

  const handleMarkAllRead = async () => {
    await Promise.all([
      markAllNotificationsRead().catch(() => {}),
      markAllAnnouncementsRead().catch(() => {}),
    ]);
    setNotifications(prev => prev.map(n => ({ ...n, readAt: n.readAt || new Date().toISOString() })));
    setAnnouncements(prev => prev.map(a => ({ ...a, isRead: true })));
    refreshUnread();
  };

  const handleDismissPopup = async (item: PopupItem) => {
    if (item.kind === 'announcement') {
      await markAnnouncementRead(item.id).catch(() => {});
    } else {
      await markNotificationRead(item.id).catch(() => {});
    }
    setPopup(null);
    clearLatestEvent();
    refreshUnread();
  };

  // 直接点击新通知气泡：与铃铛下拉里点击该条通知行为一致——标记已读并收起；
  // 工单类通知再跳转到对应工单详情。省去「关掉气泡→点铃铛→找到这条→再点进去」。
  const handlePopupClick = async (item: PopupItem) => {
    if (item.kind === 'announcement') {
      await markAnnouncementRead(item.id).catch(() => {});
    } else {
      await markNotificationRead(item.id).catch(() => {});
    }
    setPopup(null);
    clearLatestEvent();
    refreshUnread();
    if (item.kind === 'notification' && item.relatedType === 'ticket' && item.relatedId) {
      navigate(`/tickets/${item.relatedId}`);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent, onClick: () => void) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick();
    }
  };

  const tabStyle = (active: boolean): React.CSSProperties => ({
    flex: 1,
    padding: '0.5rem 0',
    fontSize: '0.82rem',
    fontWeight: active ? 600 : 400,
    color: active ? 'var(--primary)' : 'var(--text-3)',
    background: 'transparent',
    border: 'none',
    borderBottom: active ? '2px solid var(--primary)' : '2px solid transparent',
    cursor: 'pointer',
    transition: 'all var(--transition-fast)',
  });

  return (
    <div ref={dropdownRef} className="dropdown" style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(v => !v)}
        className="btn-ghost"
        aria-label={unread.total > 0 ? t('bell.ariaLabelWithCount', { count: unread.total }) : t('bell.ariaLabel')}
        aria-expanded={open}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          width: '36px', height: '36px', borderRadius: '8px',
          background: open ? 'var(--primary-light)' : 'transparent',
          border: `1px solid ${open ? 'var(--primary)' : 'transparent'}`,
          cursor: 'pointer', transition: 'all var(--transition-fast)',
          color: open ? 'var(--primary)' : 'var(--text-3)',
          padding: 0, position: 'relative',
        }}
        title={t('bell.title')}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
          <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
        </svg>
        {unread.total > 0 && (
          <span style={{
            position: 'absolute', top: '-2px', right: '-2px',
            background: 'var(--danger)',
            color: '#fff', fontSize: '10px', fontWeight: 700,
            minWidth: '16px', height: '16px', lineHeight: '16px',
            borderRadius: '8px', textAlign: 'center', padding: '0 4px',
          }}>
            {unread.total > MAX_BADGE_COUNT ? `${MAX_BADGE_COUNT}+` : unread.total}
          </span>
        )}
      </button>

      {popup && !open && ReactDOM.createPortal(
        <div
          role="button"
          tabIndex={0}
          aria-live="polite"
          onClick={() => handlePopupClick(popup)}
          onKeyDown={e => handleKeyDown(e, () => handlePopupClick(popup))}
          onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.background = 'var(--surface-2)'; }}
          onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.background = 'var(--surface)'; }}
          style={{
            position: 'fixed',
            top: '56px',
            right: '0.75rem',
            width: 'min(360px, calc(100vw - 1.5rem))',
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: '8px',
            boxShadow: 'var(--shadow-lg)',
            // 高于 modal 遮罩(.modal-overlay z-index:1000)与 toast(2000)，
            // 确保新建项目等强制弹窗的纯色底不会挡住关键通知（如剧本不合法提示），
            // 否则用户会卡死在弹窗里看不到原因。
            // 必须 portal 到 body：header 有 backdrop-filter + z-index:100，
            // 自成层叠上下文，若留在 header 子树里本 zIndex 对外不生效，
            // 整个气泡会以 100 参与排序而被遮罩(1000)盖住。
            zIndex: 2100,
            padding: '0.8rem 0.9rem',
            cursor: 'pointer',
            transition: 'background var(--transition-fast)',
          }}
        >
          <button
            type="button"
            aria-label={t('bell.dismissNewItem')}
            onClick={e => { e.stopPropagation(); handleDismissPopup(popup); }}
            style={{
              position: 'absolute',
              top: '0.45rem',
              right: '0.45rem',
              width: '24px',
              height: '24px',
              border: 'none',
              borderRadius: '6px',
              background: 'transparent',
              color: 'var(--text-3)',
              cursor: 'pointer',
              fontSize: '1rem',
              lineHeight: 1,
            }}
          >
            ×
          </button>
          <div style={{ paddingRight: '1.6rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', marginBottom: '0.35rem' }}>
              <span style={{
                fontSize: '0.7rem',
                padding: '0.1rem 0.4rem',
                borderRadius: '4px',
                fontWeight: 600,
                color: popup.kind === 'announcement'
                  ? (ANNOUNCEMENT_TYPE_COLORS[popup.type as Announcement['type']] || ANNOUNCEMENT_TYPE_COLORS.update)
                  : (NOTIFICATION_TYPE_COLORS[popup.type] || 'var(--text-3)'),
                background: 'var(--primary-light)',
              }}>
                {popup.kind === 'announcement' ? getAnnouncementTypeLabel(popup.type) : getNotificationTypeLabel(popup.type)}
              </span>
              {popup.createdAt && (
                <span style={{ fontSize: '0.74rem', color: 'var(--text-3)', marginLeft: 'auto' }}>
                  {formatTime(popup.createdAt)}
                </span>
              )}
            </div>
            <div style={{ fontSize: '0.9rem', fontWeight: 650, color: 'var(--text)', marginBottom: popup.content ? '0.25rem' : 0 }}>
              {popup.title}
            </div>
            {popup.content && (
              <div style={{
                fontSize: '0.8rem',
                color: 'var(--text-2)',
                lineHeight: 1.45,
                overflow: 'hidden',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical' as const,
              }}>
                {popup.content}
              </div>
            )}
          </div>
        </div>,
        document.body
      )}

      {open && (
        <div className="dropdown-menu" role="dialog" aria-label={t('bell.panelLabel')} style={{
          position: 'fixed', top: 'auto', marginTop: '4px',
          width: 'min(380px, calc(100vw - 1rem))',
          maxHeight: 'min(500px, 70vh)',
          display: 'block', zIndex: 200,
          right: '0.5rem',
        }}>
          {/* Tab 栏 */}
          <div style={{
            display: 'flex',
            borderBottom: '1px solid var(--border)',
          }} role="tablist">
            <button
              role="tab"
              aria-selected={tab === 'notifications'}
              style={tabStyle(tab === 'notifications')}
              onClick={() => setTab('notifications')}
            >
              {unread.notifications > 0 ? t('bell.tabNotificationsCount', { count: unread.notifications }) : t('bell.tabNotifications')}
            </button>
            <button
              role="tab"
              aria-selected={tab === 'announcements'}
              style={tabStyle(tab === 'announcements')}
              onClick={() => setTab('announcements')}
            >
              {unread.announcements > 0 ? t('bell.tabAnnouncementsCount', { count: unread.announcements }) : t('bell.tabAnnouncements')}
            </button>
          </div>

          {/* 内容区域 */}
          <div style={{ maxHeight: '400px', overflowY: 'auto' }} role="tabpanel">
            {loading ? (
              <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-3)', fontSize: '0.85rem' }}>
                {t('bell.loading')}
              </div>
            ) : error ? (
              <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--danger)', fontSize: '0.85rem' }}>
                {error}
              </div>
            ) : tab === 'announcements' ? (
              renderAnnouncements()
            ) : (
              renderNotifications()
            )}
          </div>

          {/* 底部操作栏 */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            borderTop: '1px solid var(--border)',
          }}>
            {unread.total > 0 && (
              <button
                onClick={handleMarkAllRead}
                style={{
                  flex: 1,
                  padding: '0.6rem 1rem',
                  fontSize: '0.82rem',
                  color: 'var(--text-2)',
                  background: 'none',
                  border: 'none',
                  borderRight: '1px solid var(--border)',
                  cursor: 'pointer',
                  fontWeight: 500,
                }}
              >
                {t('bell.markAllRead')}
              </button>
            )}
            <div
              role="button"
              tabIndex={0}
              onClick={() => { setOpen(false); navigate('/notifications'); }}
              onKeyDown={e => handleKeyDown(e, () => { setOpen(false); navigate('/notifications'); })}
              style={{
                flex: 1,
                padding: '0.6rem 1rem',
                textAlign: 'center',
                fontSize: '0.82rem',
                color: 'var(--primary)',
                cursor: 'pointer',
                fontWeight: 500,
              }}
            >
              {t('bell.viewAll')}
            </div>
          </div>
        </div>
      )}
    </div>
  );

  function renderAnnouncements() {
    if (announcements.length === 0) {
      return (
        <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-3)', fontSize: '0.85rem' }}>
          {t('bell.emptyAnnouncements')}
        </div>
      );
    }
    return announcements.map(a => {
      const aColor = ANNOUNCEMENT_TYPE_COLORS[a.type] || ANNOUNCEMENT_TYPE_COLORS.update;
      const aLabel = getAnnouncementTypeLabel(a.type);
      const clickHandler = () => !a.isRead && handleReadAnnouncement(a.id);
      return (
        <div
          key={a.id}
          role={a.isRead ? undefined : 'button'}
          tabIndex={a.isRead ? undefined : 0}
          onClick={clickHandler}
          onKeyDown={a.isRead ? undefined : e => handleKeyDown(e, clickHandler)}
          style={{
            padding: '0.75rem 1rem',
            borderBottom: '1px solid var(--border)',
            cursor: a.isRead ? 'default' : 'pointer',
            background: a.isRead ? 'transparent' : 'var(--primary-light)',
            transition: 'background var(--transition-fast)',
          }}
          onMouseEnter={e => { if (!a.isRead) (e.currentTarget as HTMLDivElement).style.background = 'var(--bg-hover, rgba(0,0,0,0.03))'; }}
          onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.background = a.isRead ? 'transparent' : 'var(--primary-light)'; }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
            {!a.isRead && (
              <span style={{
                width: '6px', height: '6px', borderRadius: '50%',
                background: 'var(--primary)', flexShrink: 0,
              }} />
            )}
            <span style={{
              fontSize: '0.7rem', padding: '0.1rem 0.4rem',
              borderRadius: '4px', fontWeight: 600,
              color: aColor,
              background: `color-mix(in srgb, ${aColor} 10%, transparent)`,
            }}>
              {aLabel}
            </span>
            <span style={{ fontSize: '0.75rem', color: 'var(--text-3)', marginLeft: 'auto' }}>
              {formatTime(a.publishedAt)}
            </span>
          </div>
          <div style={{
            fontSize: '0.85rem', fontWeight: a.isRead ? 400 : 600,
            color: 'var(--text)',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {a.title}
          </div>
          {a.content && (
            <div style={{
              fontSize: '0.78rem', color: 'var(--text-3)',
              marginTop: '0.2rem',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {a.content.substring(0, 80)}
            </div>
          )}
        </div>
      );
    });
  }

  function renderNotifications() {
    if (notifications.length === 0) {
      return (
        <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-3)', fontSize: '0.85rem' }}>
          {t('bell.emptyNotifications')}
        </div>
      );
    }
    return notifications.map(n => {
      const isUnread = !n.readAt;
      const isTicketLink = n.relatedType === 'ticket' && !!n.relatedId;
      const isClickable = isUnread || isTicketLink;
      const nColor = NOTIFICATION_TYPE_COLORS[n.type] || 'var(--text-3)';
      const nLabel = getNotificationTypeLabel(n.type);
      const clickHandler = () => {
        if (isUnread) handleReadNotification(n.id);
        if (n.relatedType === 'ticket' && n.relatedId) {
          setOpen(false);
          navigate(`/tickets/${n.relatedId}`);
        }
      };
      return (
        <div
          key={n.id}
          role={isClickable ? 'button' : undefined}
          tabIndex={isClickable ? 0 : undefined}
          onClick={clickHandler}
          onKeyDown={isClickable ? e => handleKeyDown(e, clickHandler) : undefined}
          style={{
            padding: '0.75rem 1rem',
            borderBottom: '1px solid var(--border)',
            cursor: isClickable ? 'pointer' : 'default',
            background: isUnread ? 'var(--primary-light)' : 'transparent',
            transition: 'background var(--transition-fast)',
          }}
          onMouseEnter={e => { if (isUnread) (e.currentTarget as HTMLDivElement).style.background = 'var(--bg-hover, rgba(0,0,0,0.03))'; }}
          onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.background = isUnread ? 'var(--primary-light)' : 'transparent'; }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
            {isUnread && (
              <span style={{
                width: '6px', height: '6px', borderRadius: '50%',
                background: 'var(--primary)', flexShrink: 0,
              }} />
            )}
            <span style={{
              fontSize: '0.7rem', padding: '0.1rem 0.4rem',
              borderRadius: '4px', fontWeight: 600,
              color: nColor,
              background: `color-mix(in srgb, ${nColor} 10%, transparent)`,
            }}>
              {nLabel}
            </span>
            {n.priority === 'high' && (
              <span style={{
                fontSize: '0.65rem', padding: '0.05rem 0.3rem',
                borderRadius: '3px', fontWeight: 600,
                color: 'var(--danger)',
                border: '1px solid var(--danger)',
              }}>
                {t('bell.important')}
              </span>
            )}
            <span style={{ fontSize: '0.75rem', color: 'var(--text-3)', marginLeft: 'auto' }}>
              {formatTime(n.createdAt)}
            </span>
          </div>
          <div style={{
            fontSize: '0.85rem', fontWeight: isUnread ? 600 : 400,
            color: 'var(--text)',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {n.title}
          </div>
          {n.content && (
            <div style={{
              fontSize: '0.78rem', color: 'var(--text-3)',
              marginTop: '0.2rem',
              overflow: 'hidden', textOverflow: 'ellipsis',
              display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' as const,
            }}>
              {n.content}
            </div>
          )}
        </div>
      );
    });
  }
}
